import type { ProfileDetail } from "@shared/types";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import {
	AlertTriangle,
	Ban,
	Heart,
	Lock,
	MapPin,
	MessageSquare,
	ShieldCheck,
	UserPlus,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useBlockProfile, useUnblockProfile } from "@/api/mutations/profile";
import { usePublicConfig } from "@/api/queries/config";
import { useProfile, useProfileInfo } from "@/api/queries/profile";
import { LoginModal } from "@/components/auth/LoginModal";
import {
	ProfileActions,
	ProfileAttributes,
	ProfileHeader,
	ProfileImages,
} from "@/components/profile";
import { ConfirmDialog } from "@/components/shared/ConfirmDialog";
import { EmptyState } from "@/components/shared/EmptyState";
import { ImageGallery } from "@/components/shared/ImageGallery";
import { Avatar } from "@/components/ui/Avatar";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { Spinner } from "@/components/ui/Spinner";
import { usePageMeta } from "@/hooks/use-page-meta";
import { hasUserCookie } from "@/lib/auth-cookie";

export const Route = createFileRoute("/_public/profile/$name")({
	component: ProfilePage,
	head: () => ({
		meta: [{ name: "robots", content: "noindex, follow" }],
	}),
});

function ProfilePage() {
	const { name } = Route.useParams();
	const isLoggedIn = hasUserCookie();

	const { data: profile, isLoading, isError } = useProfile(name);
	const { data: config } = usePublicConfig();

	usePageMeta({
		title:
			config && profile
				? isLoggedIn
					? config.seo.titles.profile.replace("%profile.name%", profile.name)
					: config.seo.titles.preview.replace("%profile.name%", profile.name)
				: undefined,
		noIndex: true,
	});

	if (isLoading) {
		return (
			<div className="flex items-center justify-center py-20">
				<Spinner size="lg" label="Profil wird geladen" />
			</div>
		);
	}

	if (isError || !profile) {
		if (isLoggedIn) {
			return (
				<EmptyState
					icon={<AlertTriangle size={48} />}
					title="Profil nicht gefunden"
					description="Das Profil konnte nicht geladen werden oder existiert nicht."
					action={
						<Button variant="outline" onClick={() => window.history.back()}>
							Zurück zur Suche
						</Button>
					}
				/>
			);
		}
		return (
			<EmptyState
				icon={<Lock size={48} />}
				title="Profil nicht verfügbar"
				description="Dieses Profil konnte nicht geladen werden."
				action={
					<Link to="/login">
						<Button variant="primary">Zum Login</Button>
					</Link>
				}
			/>
		);
	}

	if (isLoggedIn) {
		return <AuthenticatedProfileView profile={profile} name={name} />;
	}
	return <PublicPreviewView profile={profile} name={name} />;
}

// -- Authenticated View (eingeloggte User) --

function AuthenticatedProfileView({ profile, name }: { profile: ProfileDetail; name: string }) {
	const navigate = useNavigate();
	const blockProfile = useBlockProfile();
	const unblockProfile = useUnblockProfile();
	const { data: profileInfo } = useProfileInfo(name);

	const [showBlockDialog, setShowBlockDialog] = useState(false);
	const [showUnblockDialog, setShowUnblockDialog] = useState(false);
	const [galleryStartIndex, setGalleryStartIndex] = useState(0);
	const [showGallery, setShowGallery] = useState(false);

	const handleBlock = () => {
		blockProfile.mutate(
			{ id: profile.id, name: profile.name },
			{
				onSuccess: () => {
					setShowBlockDialog(false);
					navigate({ to: "/search" });
				},
			},
		);
	};

	const handleUnblock = () => {
		unblockProfile.mutate(
			{ id: profile.id, name: profile.name },
			{
				onSuccess: () => {
					setShowUnblockDialog(false);
				},
			},
		);
	};

	const handleProfileImageClick = () => {
		setGalleryStartIndex(0);
		setShowGallery(true);
	};

	const images = profile.profile?.images ?? [];

	return (
		<div className="max-w-3xl mx-auto space-y-6 py-6 px-4">
			{/* Header mit Profilbild und Infos */}
			<Card padding="lg">
				<ProfileHeader profile={profile} onImageClick={handleProfileImageClick} />
			</Card>

			{/* Aktionen */}
			{!profile.isBlocked && (
				<Card padding="md">
					<ProfileActions profile={profile} />
				</Card>
			)}

			{/* Beschreibung */}
			{profile.profile?.aboutMe && (
				<Card header="Über mich" padding="md">
					<p className="text-text-secondary whitespace-pre-line leading-relaxed">
						{profile.profile.aboutMe}
					</p>
				</Card>
			)}

			{/* Bilder */}
			{!profile.isBlocked && images.length > 0 && (
				<Card header={`Fotos (${images.length})`} padding="md">
					<ProfileImages images={images} />
				</Card>
			)}

			{/* Attribute / Steckbrief */}
			{!profile.isBlocked && profileInfo?.attributes && (
				<ProfileAttributes attributes={profileInfo.attributes} />
			)}

			{/* Block-Button */}
			<div className="pt-4 border-t border-border/50">
				{profile.isBlocked ? (
					<Button
						variant="outline"
						size="sm"
						leftIcon={<ShieldCheck size={16} />}
						onClick={() => setShowUnblockDialog(true)}
						disabled={unblockProfile.isPending}
					>
						Blockierung aufheben
					</Button>
				) : (
					<Button
						variant="danger"
						size="sm"
						leftIcon={<Ban size={16} />}
						onClick={() => setShowBlockDialog(true)}
						disabled={blockProfile.isPending}
						className="text-base"
					>
						Profil blockieren
					</Button>
				)}
			</div>

			{/* Galerie-Modal für Profilbild-Klick im Header */}
			<ImageGallery
				images={images}
				initialIndex={galleryStartIndex}
				open={showGallery}
				onClose={() => setShowGallery(false)}
			/>

			{/* Block Dialog */}
			<ConfirmDialog
				open={showBlockDialog}
				onConfirm={handleBlock}
				onCancel={() => setShowBlockDialog(false)}
				title="Profil blockieren"
				message="Möchtest du dieses Profil wirklich blockieren? Du wirst dieses Profil nicht mehr sehen."
				confirmLabel="Blockieren"
				danger
				loading={blockProfile.isPending}
			/>

			{/* Unblock Dialog */}
			<ConfirmDialog
				open={showUnblockDialog}
				onConfirm={handleUnblock}
				onCancel={() => setShowUnblockDialog(false)}
				title="Blockierung aufheben"
				message="Möchtest du die Blockierung dieses Profils aufheben?"
				confirmLabel="Aufheben"
				loading={unblockProfile.isPending}
			/>
		</div>
	);
}

// -- Public Preview View (nicht eingeloggte User) --

function PublicPreviewView({ profile, name }: { profile: ProfileDetail; name: string }) {
	const [showLogin, setShowLogin] = useState(false);
	const autoLoginTimer = useRef<ReturnType<typeof setTimeout>>(null);

	// Auto-LoginModal nach 2s
	useEffect(() => {
		autoLoginTimer.current = setTimeout(() => {
			setShowLogin(true);
		}, 2000);

		return () => {
			if (autoLoginTimer.current) {
				clearTimeout(autoLoginTimer.current);
			}
		};
	}, []);

	const handleActionClick = () => {
		setShowLogin(true);
	};

	const images = profile.profile?.images ?? [];

	return (
		<>
			<div className="max-w-3xl mx-auto space-y-6 py-6 px-4">
				{/* Header */}
				<Card padding="lg">
					<PublicProfileHeader profile={profile} />
				</Card>

				{/* Aktions-Buttons (alle öffnen LoginModal) */}
				<Card padding="md">
					<div className="flex items-center gap-3 flex-wrap">
						<Button
							variant="outline"
							size="sm"
							leftIcon={<MessageSquare size={16} />}
							onClick={handleActionClick}
						>
							Nachricht
						</Button>
						<Button
							variant="primary"
							size="sm"
							leftIcon={<UserPlus size={16} />}
							onClick={handleActionClick}
						>
							Freundschaftsanfrage
						</Button>
						<Button
							variant="ghost"
							size="sm"
							leftIcon={<Heart size={16} className="text-text-muted" />}
							onClick={handleActionClick}
						>
							Favorisieren
						</Button>
					</div>
				</Card>

				{/* Beschreibung */}
				{profile.profile?.aboutMe && (
					<Card header="Über mich" padding="md">
						<p className="text-text-secondary whitespace-pre-line leading-relaxed">
							{profile.profile.aboutMe}
						</p>
					</Card>
				)}

				{/* Bilder (sichtbar aber nicht klickbar) */}
				{images.length > 0 && (
					<Card header={`Fotos (${images.length})`} padding="md">
						<div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
							{images.map((image, index) => {
								// isBlurred ist der dynamische Lock-Flag vom Backend; image.isFriend
								// ist statische Metadaten und bleibt nach Befreundung gesetzt.
								const isLocked = image.isBlurred;
								return (
									<div
										key={`img-${String(index)}`}
										className="relative aspect-square rounded-lg overflow-hidden"
									>
										<img
											src={image.thumb}
											alt={`Bild ${index + 1}`}
											className={`size-full object-cover ${
												isLocked ? "brightness-[0.15] blur-sm" : ""
											}`}
											loading="lazy"
										/>
										{isLocked && (
											<div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-white/80">
												<Lock size={24} />
												<span className="text-xs font-medium">Nur für Freunde</span>
											</div>
										)}
									</div>
								);
							})}
						</div>
					</Card>
				)}

				{/* Login CTA */}
				<Card padding="lg">
					<div className="text-center space-y-3">
						<h2 className="text-lg font-semibold text-text">
							Möchtest du {profile.name} kennenlernen?
						</h2>
						<p className="text-base text-text-secondary">
							Melde dich an, um Nachrichten zu schreiben und das volle Profil zu sehen.
						</p>
						<div className="flex justify-center gap-3">
							<Link to="/login">
								<Button variant="primary">Anmelden</Button>
							</Link>
							<Link to="/register">
								<Button variant="outline">Registrieren</Button>
							</Link>
						</div>
					</div>
				</Card>
			</div>

			<LoginModal
				open={showLogin}
				onClose={() => setShowLogin(false)}
				redirectTo={`/profile/${name}`}
			/>
		</>
	);
}

// Slimmed-down ProfileHeader für Public View
function PublicProfileHeader({ profile }: { profile: ProfileDetail }) {
	const profileImage = profile.profile?.profileImage ?? null;

	return (
		<div className="flex flex-col sm:flex-row items-center sm:items-start gap-5">
			<div className="relative shrink-0">
				<div className="size-20 sm:size-30 rounded-full overflow-hidden ring-3 ring-primary/20">
					{profileImage ? (
						<img src={profileImage} alt={profile.name} className="size-full object-cover" />
					) : (
						<Avatar
							name={profile.name}
							alt={profile.name}
							size="xl"
							className="size-full [&>*]:!size-full [&>*]:!text-4xl"
						/>
					)}
				</div>
				<output
					className={`absolute bottom-1 right-1 size-4 sm:size-5 rounded-full border-3 border-surface block ${
						profile.online ? "bg-success animate-blink-online" : "bg-text-muted"
					}`}
					aria-label={profile.online ? "Online" : "Offline"}
				/>
			</div>

			<div className="flex flex-col items-center sm:items-start gap-2 min-w-0">
				<div className="flex items-center gap-3 flex-wrap justify-center sm:justify-start">
					<h1 className="text-2xl font-bold text-text">{profile.name}</h1>
					{profile.age > 0 && (
						<span className="text-lg text-text-secondary">{profile.age} Jahre</span>
					)}
					{profile.badge && (
						<Badge variant="primary" size="sm">
							{profile.badge}
						</Badge>
					)}
				</div>

				{/* Location mit Blur */}
				{profile.location?.city && (
					<div className="flex items-center gap-1.5 text-text-secondary">
						<MapPin size={16} className="shrink-0" />
						<span style={{ filter: "blur(3px)" }}>
							{profile.location.zip ? `${profile.location.zip} ` : ""}
							{profile.location.city}
						</span>
						<span className="text-xs text-text-muted ml-1">(Versteckt)</span>
					</div>
				)}

				<div className="flex items-center gap-1.5 text-text-secondary">
					<span
						className={`inline-block size-2 rounded-full ${profile.online ? "bg-success" : "bg-text-muted"}`}
						aria-hidden="true"
					/>
					<span className="text-base">{profile.online ? "Online" : "Offline"}</span>
				</div>
			</div>
		</div>
	);
}
